import type { SiteTimeline, TimelineFishGroup, TimelineTransfer } from '@/domain/fishGroups';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One transfer, as the board and the detail dialog describe it. */
export interface TransferFact {
    key: string;
    operationId: number;
    sourceId: number;
    destinationId: number;
    sourceName: string;
    destinationName: string;
    /** The source's pen on the day; null for a group the timeline has no stays of. */
    sourcePen: string | null;
    destinationPen: string | null;
    date: Date;
    count: number | null;
    /**
     * The fraction of the source group's fish that moved: the count over the source's
     * estimated size (its last counted size, else its initial population). Null without either.
     */
    share: number | null;
}

function penOn(group: TimelineFishGroup | undefined, date: Date): string | null {
    if (!group) return null;
    const stay = [...group.stays].reverse().find((s) => s.from.getTime() <= date.getTime());
    return (stay ?? group.stays[0])?.cageName ?? null;
}

function factOf(
    transfer: TimelineTransfer,
    groups: ReadonlyMap<number, TimelineFishGroup>,
    names: ReadonlyMap<number, string>
): TransferFact {
    const source = groups.get(transfer.sourceFishGroupId);
    const destination = groups.get(transfer.destinationFishGroupId);
    const sourceSize = source?.estimatedCount ?? null;
    const count = transfer.estimatedFishCount;
    return {
        key: `transfer-${transfer.id}`,
        operationId: transfer.operation.id,
        sourceId: transfer.sourceFishGroupId,
        destinationId: transfer.destinationFishGroupId,
        sourceName: names.get(transfer.sourceFishGroupId) ?? '',
        destinationName: names.get(transfer.destinationFishGroupId) ?? '',
        sourcePen: penOn(source, transfer.date),
        destinationPen: destination?.stays[0]?.cageName ?? null,
        date: transfer.date,
        count,
        share: count !== null && sourceSize !== null && sourceSize > 0 ? count / sourceSize : null,
    };
}

/** Every transfer of the timeline with its count and share, in the timeline's order. */
export function transferFacts(timeline: SiteTimeline): TransferFact[] {
    const groups = new Map(timeline.groups.map((group) => [group.id, group]));
    const names = new Map<number, string>([
        ...timeline.externalGroups.map((group) => [group.id, group.name] as const),
        ...timeline.groups.map((group) => [group.id, group.name] as const),
    ]);
    return timeline.transfers.map((transfer) => factOf(transfer, groups, names));
}

/** A group's transfers in and out, oldest first. */
export function groupTransfers(
    facts: TransferFact[],
    groupId: number
): { incoming: TransferFact[]; outgoing: TransferFact[] } {
    const byDate = (a: TransferFact, b: TransferFact) => a.date.getTime() - b.date.getTime();
    return {
        incoming: facts.filter((fact) => fact.destinationId === groupId).sort(byDate),
        outgoing: facts.filter((fact) => fact.sourceId === groupId).sort(byDate),
    };
}

export function formatShare(share: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: share < 0.01 ? 1 : 0,
    }).format(share);
}

/** "18,000 fish (3%)", "18,000 fish", or "fish count not registered". */
export function transferAmount(fact: TransferFact, locale: string, t: Translate): string {
    if (fact.count === null) return t('board.transfer.noCount');
    const count = new Intl.NumberFormat(locale).format(fact.count);
    return fact.share === null
        ? t('board.transfer.count', { count })
        : t('board.transfer.countShare', { count, share: formatShare(fact.share, locale) });
}

/**
 * "merd 05 → merd 03: 18,000 fish (3%)". Between pens, by pen; a transfer that stays in one
 * pen (a continuation drawn on its own row) is named by its groups.
 */
export function transferLabel(fact: TransferFact, locale: string, t: Translate): string {
    const byPen = fact.sourcePen && fact.destinationPen && fact.sourcePen !== fact.destinationPen;
    return t('board.transfer.label', {
        from: byPen ? fact.sourcePen : fact.sourceName,
        to: byPen ? fact.destinationPen : fact.destinationName,
        amount: transferAmount(fact, locale, t),
    });
}
